import {parseNumber} from '@benchling/js-utils';
import {useCallback, useEffect, useState} from 'react';

import {MonomerCapGroup} from 'graphql-client/__generated/graphqlGlobalTypes';

import {MonomerCapGroupWithWildcard, WILDCARD_CAP_GROUP_SYMBOL} from '../MonomerConstants';
import {MonomerValidationErrors, MonomerValidationOutput} from '../MonomersDAO';

export enum MonomerManualCreationStepKey {
  BUILD = 'BUILD',
  DEFINE = 'DEFINE',
}

// The validation-input fields shared by every monomer type. Each monomer type extends this with its own
// fields (e.g. the natural analogue, whose value type differs between nucleotide and amino-acid monomers) in
// its own DAO, and passes that concrete type as `TValidationInput`.
export interface BaseMonomerValidationFields {
  name?: string | null;
  symbol?: string | null;
  smiles?: string | null;
  capGroups?: Array<MonomerCapGroup | null> | null;
  customMolecularWeight?: number | null;
}

const EMPTY_VALIDATION_ERRORS: MonomerValidationErrors = {
  smiles: null,
  symbol: null,
  attachmentPoints: null,
  molecularWeight: null,
  structureImage: null,
  naturalAnalogue: null,
  customMolecularWeight: null,
  color: null,
};

export interface UseMonomerManualCreationStateArgs<TValidationInput extends BaseMonomerValidationFields> {
  // Validates a single monomer input. Must be stable (e.g. wrapped in useCallback) so the initial validation
  // runs only once on mount.
  runValidation: (input: TValidationInput) => Promise<MonomerValidationOutput>;
  // Returns the monomer-type-specific validation fields (e.g. the natural analogue) from the caller's define
  // state. Read at call time, so it does not need to be stable.
  getTypeSpecificFields: () => Partial<TValidationInput>;
}

export interface MonomerManualCreationState<TValidationInput extends BaseMonomerValidationFields> {
  name: string;
  symbol: string;
  smilesInput: string;
  canonicalSmiles: string | null;
  attachmentPointsInput: Array<MonomerCapGroup | null> | null;
  calculatedAttachmentPoints: Array<MonomerCapGroupWithWildcard> | null;
  customMolecularWeight: string | null;
  calculatedMolecularWeight: number | null;
  editingMolecularWeight: boolean;
  structureImage: string | null;
  validationErrors: MonomerValidationErrors;
  hasCustomWeightError: boolean;
  buildPhaseCompleted: boolean;
  definePhaseCompleted: boolean;
  onChangeSmilesInput: (smiles: string) => void;
  onChangeAttachmentPointsInput: (attachmentPoints: Array<MonomerCapGroup | null> | null) => void;
  onChangeName: (name: string) => void;
  onChangeSymbol: (symbol: string) => void;
  onChangeCustomMolecularWeight: (weight: string | null) => void;
  setEditingMolecularWeight: (editing: boolean) => void;
  setHasCustomWeightError: (hasError: boolean) => void;
  // Runs validation with the current base fields plus the type-specific fields. Callers should invoke this after
  // mutating define state that affects validation (e.g. the natural analogue), passing the changed value via
  // `overrides` to avoid reading stale state; changes that should not revalidate (e.g. visual color) skip it.
  validate: (overrides?: Partial<TValidationInput>) => void;
}

// Owns the shared state and validation orchestration for the monomer manual-creation wizard. The two subtle
// rules preserved here: SMILES edits (and the initial load) validate with only `{smiles}`, while all other
// edits send the full input; and only attachment-point changes sync the SMILES input to the canonical form.
export function useMonomerManualCreationState<TValidationInput extends BaseMonomerValidationFields>({
  runValidation,
  getTypeSpecificFields,
}: UseMonomerManualCreationStateArgs<TValidationInput>): MonomerManualCreationState<TValidationInput> {
  const [smilesInput, setSmilesInput] = useState('');
  const [canonicalSmiles, setCanonicalSmiles] = useState<string | null>(null);
  const [customMolecularWeight, setCustomMolecularWeight] = useState<string | null>(null);
  const [calculatedMolecularWeight, setCalculatedMolecularWeight] = useState<number | null>(null);
  const [editingMolecularWeight, setEditingMolecularWeight] = useState(false);
  const [attachmentPointsInput, setAttachmentPointsInput] = useState<Array<MonomerCapGroup | null> | null>(
    null
  );
  const [calculatedAttachmentPoints, setCalculatedAttachmentPoints] =
    useState<Array<MonomerCapGroupWithWildcard> | null>([]);
  const [symbol, setSymbol] = useState('');
  const [name, setName] = useState('');
  const [structureImage, setStructureImage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<MonomerValidationErrors>(EMPTY_VALIDATION_ERRORS);
  const [hasCustomWeightError, setHasCustomWeightError] = useState(false);
  const [buildPhaseCompleted, setBuildPhaseCompleted] = useState(false);
  const [definePhaseCompleted, setDefinePhaseCompleted] = useState(false);

  const doValidateAndSetValues = useCallback(
    async (
      monomerValidationInput: TValidationInput,
      syncSmilesInputWithCanonicalSmiles: boolean
    ): Promise<void> => {
      const {properties, errors, isValid} = await runValidation(monomerValidationInput);
      setCanonicalSmiles(properties.canonicalSmiles);
      setCalculatedAttachmentPoints(properties.attachmentPoints);
      setAttachmentPointsInput(
        properties.attachmentPoints?.map((attachmentPoint) =>
          attachmentPoint !== WILDCARD_CAP_GROUP_SYMBOL ? attachmentPoint : null
        ) ?? null
      );
      setCalculatedMolecularWeight(properties.molecularWeight);
      setStructureImage(properties.structureImage);

      setValidationErrors(errors);

      const anyBuildPhaseErrors = errors.smiles || errors.attachmentPoints;
      setBuildPhaseCompleted(!anyBuildPhaseErrors);
      setDefinePhaseCompleted(isValid);
      if (syncSmilesInputWithCanonicalSmiles && properties.canonicalSmiles != null) {
        setSmilesInput(properties.canonicalSmiles);
      }
    },
    [runValidation]
  );

  const buildFullValidationInput = (overrides: Partial<TValidationInput> = {}): TValidationInput =>
    ({
      name,
      symbol,
      smiles: smilesInput,
      capGroups: attachmentPointsInput,
      customMolecularWeight: customMolecularWeight != null ? parseNumber(customMolecularWeight) : null,
      ...getTypeSpecificFields(),
      ...overrides,
    }) as TValidationInput;

  useEffect(() => {
    doValidateAndSetValues({smiles: ''} as TValidationInput, false);
  }, [doValidateAndSetValues]);

  return {
    name,
    symbol,
    smilesInput,
    canonicalSmiles,
    attachmentPointsInput,
    calculatedAttachmentPoints,
    customMolecularWeight,
    calculatedMolecularWeight,
    editingMolecularWeight,
    structureImage,
    validationErrors,
    hasCustomWeightError,
    buildPhaseCompleted,
    definePhaseCompleted,
    setEditingMolecularWeight,
    setHasCustomWeightError,
    onChangeSmilesInput: (newSmilesInput: string): void => {
      setSmilesInput(newSmilesInput);
      doValidateAndSetValues({smiles: newSmilesInput} as TValidationInput, false);
    },
    onChangeAttachmentPointsInput: (newAttachmentPointsInput: Array<MonomerCapGroup | null> | null): void => {
      setAttachmentPointsInput(newAttachmentPointsInput);
      doValidateAndSetValues(
        buildFullValidationInput({capGroups: newAttachmentPointsInput} as Partial<TValidationInput>),
        true
      );
    },
    onChangeName: (newName: string): void => {
      setName(newName);
      doValidateAndSetValues(buildFullValidationInput({name: newName} as Partial<TValidationInput>), false);
    },
    onChangeSymbol: (newSymbol: string): void => {
      setSymbol(newSymbol);
      doValidateAndSetValues(
        buildFullValidationInput({symbol: newSymbol} as Partial<TValidationInput>),
        false
      );
    },
    onChangeCustomMolecularWeight: (newCustomMolecularWeight: string | null): void => {
      setCustomMolecularWeight(newCustomMolecularWeight);
      doValidateAndSetValues(
        buildFullValidationInput({
          customMolecularWeight:
            newCustomMolecularWeight != null ? parseNumber(newCustomMolecularWeight) : null,
        } as Partial<TValidationInput>),
        false
      );
    },
    validate: (overrides: Partial<TValidationInput> = {}): void => {
      doValidateAndSetValues(buildFullValidationInput(overrides), false);
    },
  };
}
